import { Tooltip } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLLoadItem } from '@store/ltl/types';
import { openModal } from '@store/modals';
import { orderStore } from '@store/orders';
import {
  LtlQuoteLoadItemViewModel,
  LtlQuoteParametersViewModel,
} from '@store/orders/types';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isOrderStoreDirty } from '@utils/utils';
import { createEffect, createSignal, on, Show } from 'solid-js';

import {
  isRequoteNeeded,
  type ItemDifference,
} from './LoadInfo/validateRequoteNeeded';

type Props = {
  load: ILoadEditViewModel;
  ltlQuoteParams?: LtlQuoteParametersViewModel;
  loadLTLItems?: LtlQuoteLoadItemViewModel[];
  isLoading?: boolean;
};

const SAVE_BEFORE_QUOTING_MSG = 'Please save the load prior to quoting';

const getRequoteMessage = (differences: ItemDifference[]): string => {
  if (!differences.length) {
    return 'Load data has changed since the quote. Please save before requoting.';
  }

  const fieldNameMap: Record<string, string> = {
    itemCount: 'Item count',
    class: 'Freight class',
    weight: 'Weight',
    length: 'Length',
    width: 'Width',
    height: 'Height',
    quantity: 'Quantity',
    hazmat: 'Hazmat status',
    breakdownCount: 'Sub-item count',
    breakdownWeight: 'Weight',
    breakdownPieces: 'Pieces',
    breakdownClass: 'Freight class',
    breakdownHazmat: 'Hazmat status',
    originCountry: 'Origin country',
    originState: 'Origin state',
    originZip: 'Origin zip code',
    destinationCountry: 'Destination country',
    destinationState: 'Destination state',
    destinationZip: 'Destination zip code',
  };

  const significantDifferences = differences.slice(0, 3);
  let message = 'Load data changes require save before requoting: ';

  significantDifferences.forEach((diff, index) => {
    const fieldName = fieldNameMap[diff.fieldName] ?? diff.fieldName;
    if (index > 0) message += ', ';
    if (diff.index === undefined) {
      message += `${fieldName} (${diff.original} → ${diff.current})`;
    } else {
      const subItem =
        diff.subIndex === undefined ? '' : ` Sub-item ${diff.subIndex + 1}`;
      message += `Item ${diff.index + 1}${subItem} ${fieldName} (${diff.original} → ${diff.current})`;
    }
  });

  if (differences.length > 3) {
    message += `, and ${differences.length - 3} more changes.`;
  }

  return message + ' Please save before requoting.';
};

const getDirtyQuoteErrorMessage = (
  load: ILoadEditViewModel,
  ltlQuoteParams?: LtlQuoteParametersViewModel,
  loadLTLItems?: LtlQuoteLoadItemViewModel[],
): string => {
  if (!ltlQuoteParams || !loadLTLItems) return SAVE_BEFORE_QUOTING_MSG;

  const stops = load.stops || [];
  if (stops.length < 2) return SAVE_BEFORE_QUOTING_MSG;

  const originStop = stops.find((stop) => stop.pickUp === true) || stops[0];
  const destinationStop =
    stops.find((stop) => stop.pickUp === false) || stops[1];

  const validationResult = isRequoteNeeded(
    {
      loadItems: (load.items || []).filter(
        (i) => i.operationType !== 'Delete',
      ) as unknown as LTLLoadItem[],
      originCountry: originStop.countryCode ?? '',
      originState: originStop.state ?? '',
      originZip: originStop.zip,
      destinationCountry: destinationStop.countryCode ?? '',
      destinationState: destinationStop.state ?? '',
      destinationZip: destinationStop.zip,
    },
    {
      loadItems: loadLTLItems,
      originCountry: ltlQuoteParams.originCountry,
      originState: ltlQuoteParams.originState,
      originZip: ltlQuoteParams.originZip,
      destinationCountry: ltlQuoteParams.destinationCountry,
      destinationState: ltlQuoteParams.destinationState,
      destinationZip: ltlQuoteParams.destinationZip,
    },
    {
      compareBreakdowns: isFeatureFlagEnabled(
        FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
      )(),
    },
  );

  if (validationResult.isRequoteNeeded) {
    return getRequoteMessage(validationResult.differences);
  }
  return SAVE_BEFORE_QUOTING_MSG;
};

export const GetRates = (props: Props) => {
  const isNewOrder = () => !orderStore.order.id || orderStore.order.id === 0;

  const hasQuote = () => {
    return (props.ltlQuoteParams?.id ?? 0) > 0;
  };
  const [isQuoteSelected, setIsQuoteSelected] = createSignal<boolean>(
    Boolean(props.load.quoteId),
  );

  createEffect(
    on(
      () => props.load.quoteId,
      (nextQuoteId) => {
        if (isNewOrder()) {
          setIsQuoteSelected(true);
          return;
        }
        setIsQuoteSelected(Boolean(nextQuoteId));
      },
    ),
  );

  const buttonLabel = () => (hasQuote() ? 'Requote Shipment' : 'Get LTL Rates');

  const handleV4Click = () => {
    if (isNewOrder()) return;

    if (isOrderStoreDirty()) {
      const message = hasQuote()
        ? getDirtyQuoteErrorMessage(
            props.load,
            props.ltlQuoteParams,
            props.loadLTLItems,
          )
        : SAVE_BEFORE_QUOTING_MSG;
      handleToast(ToastType.Error, message);
      return;
    }

    openModal('ltl-quoting-modal');
  };

  return (
    <div>
      <Show
        when={buttonLabel() === 'Requote Shipment' ? true : !isQuoteSelected()}
        fallback={
          <Tooltip
            title={SAVE_BEFORE_QUOTING_MSG}
            placement="left"
            class="z-50"
            dataTestId="get-rates-save-before-quoting-tooltip-legacy"
          >
            <Button
              label={buttonLabel()}
              disabled={true}
              dataTestId={formatDataTestId(buttonLabel(), 'button')}
            />
          </Tooltip>
        }
      >
        <Button
          label={buttonLabel()}
          disabled={props.isLoading}
          onClick={handleV4Click}
          dataTestId={formatDataTestId(buttonLabel(), 'button')}
        />
      </Show>
    </div>
  );
};
